/* Synchronizes the packing-team note without changing cart lines or customer text. */
(() => {
  'use strict';
  if (window.MbgInstallationNote) return;
  // Loaded before mbg-middleware: our own note requests must not trigger its observer.
  const baseFetch = window.fetch.bind(window);
  let revision = 0;
  let verifiedRevision = -1;
  let latestCart = null;
  let draft = null;
  let active = null;
  let scheduledTimer = null;
  let changingModal = false;

  function config() {
    try { return JSON.parse(document.getElementById('MbgInstallationNoteConfig')?.textContent || '{}'); }
    catch (_) { return {}; }
  }
  function failure() { return new Error(config().error || 'MBG_INSTALLATION_NOTE_UNAVAILABLE'); }
  function message() {
    const value = config().message;
    if (typeof value !== 'string' || !value.trim() || /[\r\n]/.test(value)) throw failure();
    return value;
  }
  function validCart(cart) {
    if (!cart || !Array.isArray(cart.items) || (cart.note != null && typeof cart.note !== 'string')) throw failure();
    return cart;
  }
  function compose(note, cart) {
    validCart(cart);
    const line = message();
    const escaped = line.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Remove only this reserved, exact complete line. Preserve all other text,
    // including customer whitespace, CRLFs and phrases that merely mention монтаж.
    let customer = note == null ? '' : String(note);
    customer = customer.replace(new RegExp('(?:\\r\\n|\\r|\\n)' + escaped + '(?=\\r\\n|\\r|\\n|$)', 'g'), '');
    customer = customer.replace(new RegExp('^' + escaped + '(?:\\r\\n|\\r|\\n|$)'), '');
    const requested = cart.items.some(item => item.quantity > 0 && item.properties?._mbg_installation_requested === 'true');
    return requested ? (customer ? customer + '\n' : '') + line : customer;
  }
  function endpoint(path) {
    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(root.replace(/\/?$/, '/') + path, window.location.origin);
    if (url.origin !== window.location.origin) throw failure();
    return url.href;
  }
  async function request(path, note) {
    const options = {
      credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(8000)
    };
    if (note !== undefined) {
      options.method = 'POST';
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify({ note });
    }
    const response = await baseFetch(endpoint(path), options);
    if (!response.ok) throw failure();
    return validCart(await response.json());
  }
  function nativeBusy() { return !!window.MBGCartGuard?.isPending(); }
  function updateCartEditors(note) {
    document.querySelectorAll('#x-cart-note, [data-note-input]').forEach(input => {
      // Do not move the caret or replace a new draft while a customer is typing.
      if (input !== document.activeElement && input.value !== note) input.value = note;
    });
  }
  async function reconcile() {
    const deadline = Date.now() + 15000;
    for (let attempt = 0; attempt < 6 && Date.now() < deadline; attempt++) {
      if (window.MBGCartGuard?.waitForIdle && !await window.MBGCartGuard.waitForIdle()) throw failure();
      const version = revision;
      const capturedDraft = draft;
      let cart = await request('cart.js');
      if (revision !== version || nativeBusy()) continue;
      const note = compose(capturedDraft ? capturedDraft.value : cart.note, cart);
      if (note !== (cart.note || '')) {
        cart = await request('cart/update.js', note);
        if (revision !== version || nativeBusy()) continue;
        // The write response is authoritative for both note and line markers.
        if (cart.note !== note || compose(cart.note, cart) !== note) continue;
      }
      if (revision !== version) continue;
      latestCart = cart;
      verifiedRevision = version;
      if (draft === capturedDraft) draft = null;
      updateCartEditors(cart.note || '');
      return cart;
    }
    throw failure();
  }
  function ensure(noteOverride) {
    if (typeof noteOverride === 'string') {
      draft = { value: noteOverride, source: 'override' };
      revision++;
    }
    const settle = async () => {
      const deadline = Date.now() + 15000;
      for (let attempt = 0; attempt < 6 && Date.now() < deadline; attempt++) {
        if (scheduledTimer !== null) {
          clearTimeout(scheduledTimer);
          scheduledTimer = null;
        }
        if (!active) {
          const job = reconcile().catch(() => { throw failure(); }).finally(() => {
            if (active === job) active = null;
          });
          active = job;
        }
        const cart = await active;
        // Check AFTER the shared job's then/finally microtasks. A customer edit
        // arriving during settlement must be persisted before checkout resumes.
        await Promise.resolve();
        if (verifiedRevision === revision && !nativeBusy()) return cart;
      }
      throw failure();
    };
    return settle();
  }
  function queue() {
    if (active || scheduledTimer !== null) return;
    scheduledTimer = setTimeout(() => {
      scheduledTimer = null;
      ensure().catch(() => { /* Checkout calls ensure again and displays its error. */ });
    }, 80);
  }
  function schedule() { revision++; queue(); }

  function refreshModal() {
    if (!latestCart || verifiedRevision !== revision || changingModal) return false;
    const form = document.getElementById('mw-order-form');
    if (!form) return false;
    const editor = document.getElementById('mw-note');
    const hiddenNotes = [...form.querySelectorAll('input[name="note"]')];
    // A cached vendor field is not a customer edit. Only delegated edits may
    // override the latest verified cart note; mere modal existence never does.
    const base = draft?.source === 'modal' ? draft.value : latestCart.note;
    const note = compose(base, latestCart);
    const changed = (editor && editor.value !== note) || hiddenNotes.some(input => input.value !== note);
    if (!changed) return false;
    changingModal = true;
    try {
      if (editor) {
        if (window.jQuery) window.jQuery(editor).val(note).trigger('change');
        else {
          editor.value = note;
          editor.dispatchEvent(new Event('change', { bubbles: true }));
        }
        // Vendor eu() HTML-escapes quotes even for input values. Keep the actual
        // customer text in the UI and submitted field after updating its cache B.
        editor.value = note;
      }
      hiddenNotes.forEach(input => { input.value = note; });
    } finally { changingModal = false; }
    return true;
  }
  async function prepareModal(noteOverride) {
    // No implicit read from #mw-note: it can contain the vendor's stale cache B.
    // Real edits have already been captured; an explicit override is caller-owned.
    const cart = await ensure(noteOverride);
    refreshModal();
    return cart;
  }
  function noteEdited(event) {
    if (changingModal || !(event.target instanceof Element)) return;
    const input = event.target;
    const modal = input.matches('#mw-note');
    if (!modal && !input.matches('#x-cart-note, [data-note-input]')) return;
    draft = { value: input.value, source: modal ? 'modal' : 'cart' };
    schedule();
  }
  document.addEventListener('input', noteEdited, true);
  document.addEventListener('change', noteEdited, true);
  document.addEventListener('maximize:cart:updated', schedule);
  window.MbgInstallationNote = Object.freeze({ ensure, schedule, refreshModal, prepareModal, compose });
})();
